import { apiClient } from "@/lib/api/client";

export interface PromoResponse {
  id: number;
  promo_header: string;
  promo_body: string;
  promo_duration_start: string;
  promo_duration_end: string;
  client_list: string | null;
  sent_type: 'all' | 'specific';
  status: 0 | 1; 
  current_status: 'draft' | 'ongoing' | 'upcoming' | 'expired';
  created_at: string;
  updated_at: string;
}

export interface CreatePromoRequest {
  promo_header: string;
  promo_body: string;
  promo_duration_start: string;
  promo_duration_end: string;
  is_draft?: boolean;
  send_to_all?: boolean;
  client_list?: string;
}

export interface UpdatePromoRequest {
  promo_header?: string;
  promo_body?: string;
  promo_duration_start?: string;
  promo_duration_end?: string;
  is_draft?: boolean;
  send_to_all?: boolean;
  client_list?: string;
}

export interface SendEmailRequest {
  send_to_all?: boolean;
  client_list?: string;
}

export interface PromoStats {
  ongoing: number;
  upcoming: number;
  drafts: number;
  expired: number;
  sent_to_all: number;
  sent_to_specific: number;
  total: number;
}

class PromoService {
  /**
   * Get all promos
   */
  async getAllPromos(): Promise<PromoResponse[]> {
    try {
      const response = await apiClient.get('/promos');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching promos:', error);
      throw error;
    }
  }

  /**
   * Get a single promo by ID
   */
  async getPromo(id: number): Promise<PromoResponse> {
    try {
      const response = await apiClient.get(`/promos/${id}`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching promo ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get ongoing promos
   */
  async getOngoingPromos(): Promise<PromoResponse[]> {
    try {
      const response = await apiClient.get('/promos/ongoing');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching ongoing promos:', error);
      throw error;
    }
  }

  /**
   * Get upcoming promos
   */
  async getUpcomingPromos(): Promise<PromoResponse[]> {
    try {
      const response = await apiClient.get('/promos/upcoming');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching upcoming promos:', error);
      throw error;
    }
  }

  /**
   * Get draft promos
   */
  async getDrafts(): Promise<PromoResponse[]> {
    try {
      const response = await apiClient.get('/promos/draft');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching drafts:', error);
      throw error;
    }
  }

  /**
   * Get expired promos
   */
  async getExpiredPromos(): Promise<PromoResponse[]> {
    try {
      const response = await apiClient.get('/promos/expired');
      return response.data.data;
    } catch (error) {
      console.error('Error fetching expired promos:', error);
      throw error;
    }
  }

  /**
   * Create a new promo
   */
  async createPromo(data: CreatePromoRequest): Promise<PromoResponse> {
    try {
      const response = await apiClient.post('/promos', data);
      return response.data.data.promo;
    } catch (error) {
      console.error('Error creating promo:', error);
      throw error;
    }
  }

  /**
   * Update a promo
   */
  async updatePromo(id: number, data: UpdatePromoRequest): Promise<PromoResponse> {
    try {
      const response = await apiClient.put(`/promos/${id}`, data);
      return response.data.data.promo;
    } catch (error) {
      console.error(`Error updating promo ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a promo
   */
  async deletePromo(id: number): Promise<void> {
    try {
      await apiClient.delete(`/promos/${id}`);
    } catch (error) {
      console.error(`Error deleting promo ${id}:`, error);
      throw error;
    }
  }

  /**
   * Send promo email
   */
  async sendPromoEmail(id: number, data: SendEmailRequest): Promise<any> {
    try {
      const response = await apiClient.post(`/promos/${id}/send-email`, data);
      return response.data;
    } catch (error) {
      console.error(`Error sending promo email for ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get promo statistics from backend
   */
  async getStats(): Promise<PromoStats> {
    try {
      const response = await apiClient.get('/promos/stats');
      return {
        ongoing: response.data.data.ongoing,
        upcoming: response.data.data.upcoming,
        drafts: response.data.data.drafts,
        expired: response.data.data.expired,
        sent_to_all: response.data.data.sent_to_all,
        sent_to_specific: response.data.data.sent_to_specific,
        total: response.data.data.total,
      };
    } catch (error) {
      console.error('Error getting promo stats:', error);
      throw error;
    }
  }
}

export const promoService = new PromoService();